export type NumberScaleId = "classic" | "material" | "neon" | "gits" | "gits-ice";

export interface NumberScalePalette {
    id: NumberScaleId;
    label: string;
    description: string;
    colors: [string, string, string, string, string];
    fallback: string;
    glow: number;
}

export const DEFAULT_NUMBER_SCALE_ID: NumberScaleId = "gits-ice";

export const NUMBER_SCALE_PALETTES: NumberScalePalette[] = [
    {
        id: "classic",
        label: "Classic",
        description: "Quiet red / white / green, no glow.",
        colors: ["#e57373", "#ef9a9a", "#f5f5f5", "#81c784", "#a5d6a7"],
        fallback: "rgba(255,255,255,0.45)",
        glow: 0,
    },
    {
        id: "material",
        label: "Material",
        description: "The app’s red → orange → green → blue performance scale.",
        colors: ["#F44336", "#FF8A65", "#FFA726", "#4CAF50", "#2196F3"],
        fallback: "rgba(255,255,255,0.45)",
        glow: 0,
    },
    {
        id: "neon",
        label: "Neon",
        description: "Hot signage: magenta, orange, yellow, mint, cyan.",
        colors: ["#ff2d8a", "#ff7a18", "#f5ff4a", "#2effb4", "#4df0ff"],
        fallback: "rgba(255,255,255,0.45)",
        glow: 0.7,
    },
    {
        id: "gits",
        label: "Section 9",
        description: "Ghost in the Shell HUD: sodium, steel, thermoptic teal.",
        colors: ["#c45b6a", "#c98a4e", "#8aa8a8", "#3cb8a8", "#7ee8d8"],
        fallback: "rgba(180, 210, 210, 0.45)",
        glow: 0.28,
    },
    {
        id: "gits-ice",
        label: "Thermoptic",
        description: "Pale GITS ice — rain, phosphor, almost white.",
        colors: ["#e8a8b0", "#e6c49a", "#c5d4d4", "#8ee0d6", "#e8fffb"],
        fallback: "rgba(200, 220, 220, 0.45)",
        glow: 0.22,
    },
];

export const SCALE_STOPS = {
    rate: [0.4, 0.48, 0.55, 0.62],
    rating: [0.8, 0.95, 1.1, 1.25],
    acs: [170, 200, 230, 260],
    adr: [120, 140, 160, 180],
    kd: [0.8, 0.95, 1.15, 1.4],
    hs: [0.15, 0.22, 0.28, 0.36],
};

/** Where the 5 settings-palette swatches sit for rating washes. */
export const RATING_WASH_ANCHORS = [1.1, 1.2, 1.3, 1.4, 1.5];

export function getNumberScalePalette(id?: string | null): NumberScalePalette {
    return NUMBER_SCALE_PALETTES.find((palette) => palette.id === id) || NUMBER_SCALE_PALETTES.find((palette) => palette.id === DEFAULT_NUMBER_SCALE_ID)!;
}

export function paletteWinLoss(palette: NumberScalePalette) {
    return { win: palette.colors[3], loss: palette.colors[0] };
}

export function shadeHex(hex: string, factor: number) {
    const n = hex.replace("#", "");
    const channel = (start: number) =>
        Math.max(0, Math.min(255, Math.round(parseInt(n.slice(start, start + 2), 16) * factor)));
    const toHex = (value: number) => value.toString(16).padStart(2, "0");
    return `#${toHex(channel(0))}${toHex(channel(2))}${toHex(channel(4))}`;
}

function hexToRgb(hex: string): [number, number, number] {
    const n = hex.replace("#", "");
    return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)];
}

export function mixHex(from: string, to: string, amount: number) {
    const t = Math.max(0, Math.min(1, amount));
    const [fr, fg, fb] = hexToRgb(from);
    const [tr, tg, tb] = hexToRgb(to);
    const channel = (a: number, b: number) => Math.round(a + (b - a) * t).toString(16).padStart(2, "0");
    return `#${channel(fr, tr)}${channel(fg, tg)}${channel(fb, tb)}`;
}

export function interpolateAnchored(value: number | null, anchors: number[], colors: string[], fallback: string) {
    if (value == null || Number.isNaN(value) || colors.length === 0 || anchors.length === 0) return fallback;
    const count = Math.min(anchors.length, colors.length);
    if (value <= anchors[0]) return colors[0];
    if (value >= anchors[count - 1]) return colors[count - 1];
    for (let i = 0; i < count - 1; i++) {
        if (value <= anchors[i + 1]) {
            const span = anchors[i + 1] - anchors[i] || 1;
            return mixHex(colors[i], colors[i + 1], (value - anchors[i]) / span);
        }
    }
    return colors[count - 1];
}

export function scaleColor(value: number | null, stops: number[], palette: NumberScalePalette) {
    if (value === null || Number.isNaN(value)) return palette.fallback;
    let level = 0;
    for (const stop of stops) {
        if (value >= stop) level += 1;
        else break;
    }
    return palette.colors[Math.min(level, palette.colors.length - 1)];
}

export function scaleGlowSx(color: string, glow: number) {
    if (!glow) {
        return {
            color,
            fontVariantNumeric: "tabular-nums" as const,
        };
    }
    const alpha = Math.round(Math.min(glow, 1) * 255)
        .toString(16)
        .padStart(2, "0");
    return {
        color,
        fontVariantNumeric: "tabular-nums" as const,
        textShadow: `0 0 8px ${color}${alpha}`,
    };
}
